import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { submitInquiry } from "@/lib/api";

const TYPES = [
    { id: "catering", label: "Catering" },
    { id: "private_event", label: "Private Event" },
    { id: "general", label: "General Inquiry" },
];

const EVENT_TYPES = [
    "Wedding",
    "Corporate / Office",
    "Birthday",
    "Anniversary",
    "Family Gathering",
    "Memorial",
    "Other",
];

const initialState = {
    name: "",
    email: "",
    phone: "",
    inquiry_type: "catering",
    event_date: "",
    guest_count: "",
    event_type: "",
    message: "",
};

export default function CateringForm() {
    const [form, setForm] = useState(initialState);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        const handler = (e) => {
            if (e?.detail) {
                setForm((f) => ({ ...f, inquiry_type: e.detail }));
            }
        };
        const builderHandler = (e) => {
            const d = e?.detail || {};
            setForm((f) => ({
                ...f,
                inquiry_type: d.inquiry_type || f.inquiry_type,
                guest_count: d.guest_count != null ? String(d.guest_count) : f.guest_count,
                event_type: d.event_type || f.event_type,
                message: d.message || f.message,
            }));
            // Surface success cue to the user
            try {
                const el = document.querySelector('[data-testid="catering-form"]');
                el?.classList.add("ring-1", "ring-gold/30");
                setTimeout(() => el?.classList.remove("ring-1", "ring-gold/30"), 1400);
            } catch (_) {
                /* no-op */
            }
        };
        window.addEventListener("set-inquiry-type", handler);
        window.addEventListener("set-catering-builder", builderHandler);
        return () => {
            window.removeEventListener("set-inquiry-type", handler);
            window.removeEventListener("set-catering-builder", builderHandler);
        };
    }, []);

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;

        // Minimal client validation
        if (!form.name || !form.email || !form.phone) {
            toast.error("Please complete the required fields.");
            return;
        }

        const payload = {
            ...form,
            guest_count: form.guest_count ? Number(form.guest_count) : undefined,
            event_date: form.event_date || undefined,
            event_type: form.event_type || undefined,
            message: form.message || undefined,
        };

        setSubmitting(true);
        try {
            await submitInquiry(payload);
            setSubmitted(true);
            toast.success("Thank you. We'll be in touch within one business day.");
            setForm(initialState);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(
                typeof detail === "string"
                    ? detail
                    : "Could not send your inquiry. Please call us at (845) 849-0497."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="catering-inquiry"
            data-testid="catering-form-section"
            className="relative empire-section bg-charcoal-800"
            aria-label="Catering Inquiry"
        >
            <div className="empire-container">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    {/* Intro */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9 }}
                        className="lg:col-span-5"
                    >
                        <p className="empire-eyebrow">Say Hello</p>
                        <h2
                            data-testid="form-heading"
                            className="mt-5 empire-subhead text-cream"
                        >
                            Tell us about<br />
                            <span className="italic text-gold">your evening.</span>
                        </h2>
                        <p className="empire-body mt-6 max-w-md">
                            A few lines is all we need. Michelle and the team read
                            every note, and we&rsquo;ll write you back within a
                            business day with ideas, dates, and the next steps.
                        </p>

                        <dl className="mt-12 space-y-6">
                            <div>
                                <dt className="empire-eyebrow !text-cream/55">Call</dt>
                                <dd className="font-serif text-2xl text-cream tracking-tight mt-2">
                                    (845) 849-0497
                                </dd>
                            </div>
                            <div>
                                <dt className="empire-eyebrow !text-cream/55">Email</dt>
                                <dd className="font-serif text-xl text-cream tracking-tight mt-2 break-all">
                                    Empirejamaicarestaurant@gmail.com
                                </dd>
                            </div>
                            <div>
                                <dt className="empire-eyebrow !text-cream/55">Visit</dt>
                                <dd className="font-light text-cream/85 mt-2">
                                    385 Main Street<br />
                                    Poughkeepsie, NY 12601
                                </dd>
                            </div>
                        </dl>
                    </motion.div>

                    {/* Form */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, delay: 0.15 }}
                        className="lg:col-span-7"
                    >
                        {submitted ? (
                            <div
                                data-testid="form-success"
                                className="border border-gold/40 bg-charcoal p-10 md:p-14 flex flex-col items-start gap-6"
                            >
                                <CheckCircle2 size={36} className="text-gold" strokeWidth={1.2} />
                                <h3 className="font-serif text-3xl md:text-4xl text-cream">
                                    Thank you. Your inquiry is in.
                                </h3>
                                <p className="text-cream/70 font-light max-w-md">
                                    A member of our hospitality team will be in touch
                                    within one business day. For urgent requests,
                                    please call us directly at (845) 849-0497.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setSubmitted(false)}
                                    data-testid="form-new-inquiry"
                                    className="empire-btn-secondary mt-2"
                                >
                                    Send Another Inquiry
                                </button>
                            </div>
                        ) : (
                            <form
                                onSubmit={onSubmit}
                                data-testid="catering-form"
                                className="border border-cream/10 bg-charcoal p-8 md:p-12"
                                noValidate
                            >
                                {/* Type tabs */}
                                <div
                                    role="tablist"
                                    aria-label="Inquiry type"
                                    className="flex flex-wrap gap-0 border border-cream/10 w-fit mb-10"
                                >
                                    {TYPES.map((t) => (
                                        <button
                                            type="button"
                                            key={t.id}
                                            role="tab"
                                            aria-selected={form.inquiry_type === t.id}
                                            data-testid={`form-type-${t.id}`}
                                            onClick={() =>
                                                setForm((f) => ({
                                                    ...f,
                                                    inquiry_type: t.id,
                                                }))
                                            }
                                            className={`px-5 py-3 text-[11px] uppercase tracking-[0.22em] transition-colors ${
                                                form.inquiry_type === t.id
                                                    ? "bg-burgundy text-cream"
                                                    : "text-cream/70 hover:text-gold"
                                            }`}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                                    <Field
                                        label="Full Name *"
                                        name="name"
                                        value={form.name}
                                        onChange={onChange}
                                        required
                                        autoComplete="name"
                                        testid="input-name"
                                    />
                                    <Field
                                        label="Phone *"
                                        type="tel"
                                        name="phone"
                                        value={form.phone}
                                        onChange={onChange}
                                        required
                                        autoComplete="tel"
                                        testid="input-phone"
                                    />
                                    <Field
                                        label="Email *"
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={onChange}
                                        required
                                        autoComplete="email"
                                        className="md:col-span-2"
                                        testid="input-email"
                                    />
                                    <DateField
                                        label="Event Date"
                                        name="event_date"
                                        value={form.event_date}
                                        onChange={onChange}
                                        testid="input-event-date"
                                    />
                                    <Field
                                        label="Guest Count"
                                        type="number"
                                        name="guest_count"
                                        value={form.guest_count}
                                        onChange={onChange}
                                        min="1"
                                        max="5000"
                                        placeholder="e.g. 50"
                                        testid="input-guest-count"
                                    />
                                    <div className="md:col-span-2">
                                        <label
                                            htmlFor="event_type"
                                            className="empire-label block mb-2"
                                        >
                                            Event Type
                                        </label>
                                        <select
                                            id="event_type"
                                            name="event_type"
                                            value={form.event_type}
                                            onChange={onChange}
                                            data-testid="input-event-type"
                                            className="empire-input cursor-pointer appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%23D4AF37%22 stroke-width=%221.5%22><path d=%22m6 9 6 6 6-6%22/></svg>')] bg-no-repeat bg-[right_0_center]"
                                        >
                                            <option value="" className="bg-charcoal">
                                                Select event type
                                            </option>
                                            {EVENT_TYPES.map((t) => (
                                                <option
                                                    key={t}
                                                    value={t}
                                                    className="bg-charcoal"
                                                >
                                                    {t}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="md:col-span-2">
                                        <label
                                            htmlFor="message"
                                            className="empire-label block mb-2"
                                        >
                                            Tell Us About Your Event
                                        </label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            value={form.message}
                                            onChange={onChange}
                                            rows={4}
                                            data-testid="input-message"
                                            className="empire-input resize-none"
                                            placeholder="Style of service, dietary needs, venue, etc."
                                        />
                                    </div>
                                </div>

                                <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                    <p className="text-cream/45 text-[11px] font-light">
                                        We respect your privacy. Your info is used only
                                        to respond to your inquiry.
                                    </p>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="form-submit"
                                        className="empire-btn-primary min-w-[200px] disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 size={14} className="animate-spin" />
                                                Sending…
                                            </>
                                        ) : (
                                            "Send Inquiry"
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function Field({ label, className = "", testid, ...rest }) {
    return (
        <div className={className}>
            <label htmlFor={rest.name} className="empire-label block mb-2">
                {label}
            </label>
            <input
                id={rest.name}
                {...rest}
                data-testid={testid}
                className="empire-input"
            />
        </div>
    );
}

function DateField({ label, name, value, onChange, testid, className = "" }) {
    const [type, setType] = useState("text");
    return (
        <div className={className}>
            <label htmlFor={name} className="empire-label block mb-2">
                {label}
            </label>
            <input
                id={name}
                name={name}
                type={type}
                value={value}
                onChange={onChange}
                onFocus={() => setType("date")}
                onBlur={(e) => {
                    if (!e.target.value) setType("text");
                }}
                placeholder="Select your event date"
                data-testid={testid}
                className="empire-input"
            />
        </div>
    );
}
